// Pure helpers for a member's weekly driving summary: weeks, units and the
// per-day drive list.
import type { Schemas } from "@10twenty/api";
import { dayKey, historyPath, isDayKey, shiftDay } from "../history/history";
import { distanceText, region, type Units } from "../units";

/** Regions whose week starts on Sunday, for engines without `Intl.Locale` week info (Firefox). */
const SUNDAY_REGIONS = new Set(["US", "CA", "MX", "BR", "JP", "KR", "TW", "HK", "IL", "PH", "ZA", "IN"]);

export type WeeklyStats = Schemas["WeeklyStats"];
export type WeeklyDrive = Schemas["WeeklyDrive"];
export type WeeklyStatsDay = Schemas["WeeklyStatsDay"];

type WeekInfo = { firstDay: number };

/** The locale's first day of the week, ISO numbered (1 = Monday … 7 = Sunday). */
export function firstDayOfWeek(locale?: string): number {
  try {
    const loc = new Intl.Locale(locale ?? Intl.DateTimeFormat().resolvedOptions().locale) as Intl.Locale & {
      getWeekInfo?: () => WeekInfo;
      weekInfo?: WeekInfo;
    };
    const info = loc.getWeekInfo?.() ?? loc.weekInfo;
    if (info && info.firstDay >= 1 && info.firstDay <= 7) return info.firstDay;
  } catch {
    // Fall through to the region table.
  }
  const r = region(locale);
  return r !== undefined && SUNDAY_REGIONS.has(r) ? 7 : 1;
}

/** ISO weekday (1 = Monday … 7 = Sunday) of a valid `YYYY-MM-DD` day. */
export function isoWeekday(day: string): number {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const date = new Date(0);
  date.setUTCFullYear(y, m - 1, d);
  return date.getUTCDay() === 0 ? 7 : date.getUTCDay();
}

/** The first day of the week (starting on ISO weekday `firstDay`) that contains `day`. */
export function weekOf(day: string, firstDay: number): string {
  return shiftDay(day, -((isoWeekday(day) - firstDay + 7) % 7));
}

/** The week to show for a `?week=` value: the week holding that day, or this week. */
export function resolveWeek(param: string | null, nowMs: number, firstDay: number): string {
  const today = dayKey(new Date(nowMs));
  const day = param !== null && isDayKey(param) && param <= today ? param : today;
  return weekOf(day, firstDay);
}

/** "This week", "Last week" or the week's date range ("12 – 18 Oct"). */
export function weekTitle(week: string, nowMs: number, firstDay: number, locale?: string): string {
  const current = weekOf(dayKey(new Date(nowMs)), firstDay);
  if (week === current) return "This week";
  if (week === shiftDay(current, -7)) return "Last week";
  const date = (key: string) => {
    const [y, m, d] = key.split("-").map(Number) as [number, number, number];
    const value = new Date(0);
    value.setUTCFullYear(y, m - 1, d);
    return value;
  };
  const format = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
    ...(week.slice(0, 4) === current.slice(0, 4) ? {} : { year: "numeric" }),
  });
  return format.formatRange(date(week), date(shiftDay(week, 6)));
}

/** A day of the week with driving, and its drives (none for rolled-up days). */
export interface DriveDay {
  day: WeeklyStatsDay;
  drives: WeeklyDrive[];
}

/** The days that had driving, newest first, each with its drives newest first. */
export function driveDays(stats: WeeklyStats): DriveDay[] {
  return stats.days
    .map((day) => ({
      day,
      drives: stats.drives.filter((d) => d.day === day.day).sort((a, b) => b.start.localeCompare(a.start)),
    }))
    .filter((entry) => entry.day.drive_count > 0 || entry.drives.length > 0)
    .reverse();
}

/** A weekly-stats day as "Mon 12 Oct" (it is a calendar day in the member's zone). */
export function statsDayTitle(day: string, locale?: string): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const date = new Date(0);
  date.setUTCFullYear(y, m - 1, d);
  return date.toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
}

/** The viewer's history day holding the drive, with that trip selected. */
export function drivePath(circleId: string, userId: string, drive: WeeklyDrive): string {
  const start = Date.parse(drive.start);
  return `${historyPath(circleId, userId, dayKey(new Date(start)))}&trip=${start}`;
}

export function statsPath(circleId: string, userId: string, week?: string): string {
  const path = `/circles/${encodeURIComponent(circleId)}/members/${encodeURIComponent(userId)}/driving`;
  return week === undefined ? path : `${path}?week=${week}`;
}

/**
 * The zone to save on the profile, if any: the browser's, while the profile
 * has none (days are UTC until then). The phone that records the fixes keeps
 * it current; a browser elsewhere never overrides it.
 */
export function zoneToReport(profileZone: string | null | undefined, browserZone: string | undefined): string | null {
  if (profileZone !== null || !browserZone || browserZone === "UTC" || browserZone === "Etc/UTC") return null;
  return browserZone;
}

/** A week's total distance: "0 km" / "0 mi" without driving, rather than "0 m" / "0 ft". */
export function totalDistanceText(metres: number, units: Units): string {
  return metres > 0 ? distanceText(metres, units) : units === "metric" ? "0 km" : "0 mi";
}
